import {Box, Stack} from '@dropbox/dig-foundations';
import {ComponentProps, ReactNode} from 'react';

type StackGap = ComponentProps<typeof Stack>['gap'];

/**
 * Scrolling row list for a dashboard tile (News, New arrivals, Workstreams,
 * Calendar, …). Owns the vertical scroll and re-creates the horizontal
 * "bleed" the rows rely on: `overflowY: auto` forces `overflowX: auto` (per the
 * CSS spec), which would otherwise clip the rows' sideways hover-shade
 * (`marginInline: -8`). Pulling out 8 and re-padding 8 moves the clip boundary
 * back out without shifting the content.
 *
 * Use `grow` for tiles whose header sits above the list (the list fills the
 * leftover flex height); omit it when the list is the tile's only content (it
 * takes the full height directly).
 */
export const TileScroller = ({
  children,
  grow = false,
  gap = '2',
}: {
  children: ReactNode;
  /** Fill remaining flex height (`flex: 1` + `minHeight: 0`) vs. full height. */
  grow?: boolean;
  /** Row spacing on the DIG scale (defaults to `2`). */
  gap?: StackGap;
}) => (
  <Box
    style={{
      ...(grow ? {flex: 1, minHeight: 0} : {height: '100%'}),
      overflowY: 'auto',
      marginInline: -8,
      paddingInline: 8,
    }}
  >
    <Stack gap={gap}>{children}</Stack>
  </Box>
);
